import { Component, inject, ApplicationRef } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import packageJson from '../../package.json';
import { ConnectorService } from './connector.service';
import { SwPush, SwUpdate } from '@angular/service-worker';
import { NotificationsService } from './notifications.service';
import { HttpClient } from '@angular/common/http';
import { TokenMonitorService } from './services/token-monitor.service';
import { AnalyticsService } from './services/analytics.service';
import { concat, interval } from 'rxjs';
import { first } from 'rxjs/operators';
import { routeAnimations } from './animations/route-animations';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-sociochaux',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeAnimations]
})
export class AppComponent {
  title = 'Sociochaux : Espaces Personnels';
  public version: string = packageJson.version;

  eventSource:EventSource|null = null;

  constructor(
    private connector: ConnectorService,
    private swPush: SwPush,
    private swUpdate: SwUpdate,
    public notificationsService: NotificationsService,
    private http: HttpClient,
    private tokenMonitor: TokenMonitorService,
    private appRef: ApplicationRef,
    private analytics: AnalyticsService
  ){}

  ngOnInit(): void {
    // Masquer le splash screen Capacitor après initialisation
    this.hideSplashScreen();

    // Démarrer la surveillance du token
    this.tokenMonitor.startMonitoring();

    // Statistiques d'usage (pages vues, provenance)
    this.analytics.start();
    
    // Gérer les mises à jour du service worker
    this.checkForUpdates();
    
    // Natif (Android/iOS) : notifications via FCM, sans service worker — la condition
    // swPush.isEnabled empêchait l'enregistrement de l'appareil dans l'appli
    if(this.connector.isConnected() && (Capacitor.isNativePlatform() || this.swPush.isEnabled)){
      console.log('Notifications activées');
      this.subscribeToNotifications();
    }
  }

  checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker updates not enabled');
      return;
    }

    // Vérifier les mises à jour quand l'app est stable
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    everySixHoursOnceAppIsStable$.subscribe(async () => {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log(updateFound ? 'Mise à jour trouvée' : 'Pas de mise à jour');
      } catch (err) {
        console.error('Erreur lors de la vérification des mises à jour:', err);
      }
    });

    // Écouter les mises à jour disponibles
    this.swUpdate.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') {
        console.log('Nouvelle version disponible');
        // Recharger automatiquement après 3 secondes
        setTimeout(() => {
          console.log('Rechargement pour appliquer la mise à jour...');
          window.location.reload();
        }, 3000);
      }
    });

    // Gérer les erreurs de mise à jour
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Erreur irrécupérable du Service Worker:', event.reason);
      // Proposer de recharger l'application
      if (confirm('Une erreur est survenue. Voulez-vous recharger l\'application ?')) {
        window.location.reload();
      }
    });
  }

  subscribeToNotifications() {
    this.notificationsService.subscribeToNotifications();
  }

  getYear(){
    return (new Date()).getFullYear();
  }

  isConnected(){
    return this.connector.isConnected();
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'];
  }

  private async hideSplashScreen(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch (e) {
        // Silently ignore — plugin may not be available
      }
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        // Ne pas dessiner derrière la status bar — garder le comportement PWA
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: '#004595' });
        await StatusBar.setStyle({ style: Style.Dark });
      } catch (e) {
        // Silently ignore
      }
    }
  }
}
